import { useMemo } from 'react';
import { Link, matchPath, useLocation } from 'react-router-dom';
import { Button, Menu, MenuItem, MenuTrigger } from '@backstage/ui';
import type { EntityHeaderLayoutProps } from '@backstage/plugin-catalog-react/alpha';
import styles from './HeaderTabs.module.css';

type HeaderTab = EntityHeaderLayoutProps['tabs'][number];
type HeaderTabItem = { id: string; label: string; href: string };
type HeaderTabGroup = { id: string; label: string; items: HeaderTabItem[] };

const isTabGroup = (tab: HeaderTab): tab is HeaderTabGroup => 'items' in tab;

// Tab hrefs from the blueprint are relative to the entity route
// ('security', '' for the overview) — resolve them against its base.
// The trailing /* keeps the route matching on tab sub-paths too.
const ENTITY_ROUTE = '/catalog/:namespace/:kind/:name/*';

export interface HeaderTabsProps {
  tabs: EntityHeaderLayoutProps['tabs'];
  activeTabId?: string;
}

/**
 * Entity tab row: flat tabs as links, grouped tabs as a dropdown menu.
 * Mirrors the BUI Header nav look (see the design mockups' tab bar).
 */
export function HeaderTabs({ tabs, activeTabId }: HeaderTabsProps) {
  const { pathname } = useLocation();

  const entityBase = useMemo(
    () => matchPath(ENTITY_ROUTE, pathname)?.pathnameBase ?? '',
    [pathname],
  );

  const resolveHref = (href: string) => {
    const clean = href.replace(/^\/+|\/+$/g, '');
    if (!entityBase) {
      return clean ? `/${clean}` : '/';
    }
    return clean ? `${entityBase}/${clean}` : entityBase;
  };

  // The entity page provides activeTabId; fall back to detecting the
  // deepest matching tab from the URL.
  const detectedActiveTabId = useMemo(() => {
    const matches: HeaderTabItem[] = tabs
      .flatMap(tab => (isTabGroup(tab) ? tab.items : [tab]))
      .map(item => ({ ...item, href: resolveHref(item.href) }))
      .filter(({ href }) => {
        const normalizedPathname = pathname.replace(/\/+$/, '');
        return (
          normalizedPathname === href ||
          normalizedPathname.startsWith(`${href}/`)
        );
      });
    if (matches.length === 0) {
      return undefined;
    }
    return matches.sort((a, b) => b.href.length - a.href.length)[0].id;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabs, pathname, entityBase]);

  const currentActiveId = activeTabId ?? detectedActiveTabId;
  const activeGroup = tabs.find(
    tab => isTabGroup(tab) && tab.items.some(item => item.id === currentActiveId),
  ) as HeaderTabGroup | undefined;
  const activeChildId = activeGroup?.items.find(
    item => item.id === currentActiveId,
  )?.id;

  // Outside an entity route the resolved links would point at the site
  // root — render nothing rather than wrong navigation.
  if (!entityBase) {
    return null;
  }

  return (
    <nav className={styles.nav} aria-label="Content navigation">
      <ul className={styles.list}>
        {tabs.map(tab => {
          if (isTabGroup(tab)) {
            const groupActive = tab.id === activeGroup?.id;
            return (
              <li key={tab.id}>
                <MenuTrigger>
                  <Button
                    variant="tertiary"
                    className={styles.groupTrigger}
                    data-active={groupActive || undefined}
                    aria-current={groupActive ? 'page' : undefined}
                  >
                    {tab.label}
                    <span className={styles.caret} aria-hidden="true">
                      ▾
                    </span>
                  </Button>
                  <Menu
                    selectionMode="single"
                    selectedKeys={
                      activeChildId ? new Set([activeChildId]) : new Set()
                    }
                  >
                    {tab.items.map(item => (
                      <MenuItem key={item.id} id={item.id} href={item.href}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </Menu>
                </MenuTrigger>
              </li>
            );
          }
          const active = tab.id === currentActiveId;
          return (
            <li key={tab.id}>
              <Link
                to={resolveHref(tab.href)}
                className={styles.tab}
                aria-current={active ? 'page' : undefined}
                data-active={active || undefined}
              >
                {tab.label}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
